import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Form } from '@unform/web';
import { BiX } from 'react-icons/bi';
import { ValidationError } from 'yup';
import { FormHandles } from '@unform/core';
import { IoIosWarning } from 'react-icons/io';

import Tab from '../../../../newComponents/Tab';
import TabBar from '../../../../newComponents/TabBar';
import Button from '../../../../newComponents/Button';
import { ModalHandles } from '../../../../newComponents/Modal';

import { useAuth } from '../../../../hooks/auth';
import { useToast } from '../../../../hooks/toast';
import { useSimpleProduct, Image, Option } from '../../../../hooks/simpleProduct';

import Details, { DetailsFormSchema, ORIGEM_OPTIONS } from './steps/Details';
import Price, { PriceFormSchema } from './steps/Price';
import Dimensions, { DimensionsFormSchema } from './steps/Dimensions';
import Images, { ImagesFormSchema } from './steps/Images';
import Complements, { ComplementsFormSchema } from './steps/Complements';
import Availability, { AvailabilityFormSchema } from './steps/Availability';

import getNumberValue from '../../../../utils/getNumberValue';
import getValidationErrors from '../../../../utils/getValidationErrors';

import ProductsService from '../../../../services/ProductsService';
import { Product } from '../../../../services/GroupsService';

import APIError from '../../../../errors/APIError';

import { Container, TabPanel, Modal, ModalHeader } from './styles';
import empresaApi from '../../../../services/empresaApi';
import api from '../../../../services/api';

type SimpleProductProps = {
  lastProductIndex: number;
  onSuccessSave(groupId: number, product: Product): void;
};

const LAST_TAB_INDEX = 5;

const SCHEMAS = {
  0: DetailsFormSchema,
  1: PriceFormSchema,
  2: DimensionsFormSchema,
  3: ImagesFormSchema,
  4: ComplementsFormSchema,
  5: AvailabilityFormSchema,
};

const SimpleProduct: React.FC<SimpleProductProps> = ({
  lastProductIndex,
  onSuccessSave,
}) => {
  const isFirstRender = useRef(true);
  const formRef = useRef<FormHandles>(null);
  const formMainRef = useRef<HTMLDivElement>(null);
  const modalRef = useRef<ModalHandles>(null);

  const {
    id,
    image,
    images,
    details,
    groupId,
    currentTab,
    hasDiscount,
    complements,
    groupsOptions,
    isDrawerOpened,
    isAwaysAvailability,
    unitOfMeasurementId,
    unitsOfMeasurementOptions,
    isComplementsDrawerOpened,
    handleChangeTab,
    handleCloseDrawer,
    handleResetProduct,
    ...initialSimpleProductData
  } = useSimpleProduct();
  const { addToast } = useToast();
  const { user } = useAuth();

  const [isLoading, setIsLoading] = useState(false);
  const [bestShippingIsActive, setBestShippingIsActive] = useState(false);
  const [categoriasFiscaisOptions, setCategoriasFiscaisOptions] = useState<
    Option[]
  >([]);
  const [emissaoHabilitada, setEmissaoHabilitada] = useState(false);
  const [fiscalLoaded, setFiscalLoaded] = useState(false);
  // true só quando a carga das categorias teve SUCESSO — evita apagar o vínculo
  // fiscal no save quando a API fiscal está fora do ar (A2 do code-review).
  const [fiscalLoadOk, setFiscalLoadOk] = useState(false);
  const handleCancel = useCallback(() => {
    modalRef.current?.close();
    handleCloseDrawer();

    const mainImage = document.getElementById(
      'firstSimpleProductImage',
    ) as HTMLInputElement;
    const img0 = document.getElementById(
      'simpleProductImage-0',
    ) as HTMLInputElement;
    const img1 = document.getElementById(
      'simpleProductImage-1',
    ) as HTMLInputElement;
    const img2 = document.getElementById(
      'simpleProductImage-2',
    ) as HTMLInputElement;
    const img3 = document.getElementById(
      'simpleProductImage-3',
    ) as HTMLInputElement;
    const img4 = document.getElementById(
      'simpleProductImage-4',
    ) as HTMLInputElement;
    const img5 = document.getElementById(
      'simpleProductImage-5',
    ) as HTMLInputElement;

    mainImage.value = '';
    img0.value = '';
    img1.value = '';
    img2.value = '';
    img3.value = '';
    img4.value = '';
    img5.value = '';

    setTimeout(() => {
      handleChangeTab(0);
      handleResetProduct();
      formRef.current?.reset();
      formRef.current?.setErrors({});
    }, 500);
  }, [handleCloseDrawer, handleChangeTab, handleResetProduct]);

  const handleTryCancel = useCallback(() => {
    modalRef.current?.open();
  }, []);

  useEffect(() => {
    const fetchBestShipping = async (): Promise<void> => {
      const res = await empresaApi.getParametro(
        user.empresa_id,
        'best_shipping_services',
      );
      if (res) {
        setBestShippingIsActive(JSON.parse(res.role).active);
        setBestShippingIsActive(JSON.parse(res.role).active);
      }

      if (res) {
        if (typeof res.role === 'string') {
          setBestShippingIsActive(JSON.parse(res.role).active);
        } else {
          setBestShippingIsActive(res.role.active);
        }
      }
    };

    fetchBestShipping();
  }, [user.empresa_id]);

  useEffect(() => {
    // Fiscal (Story 1-4): carrega categorias fiscais da empresa + flag de emissão.
    // Falha aqui NÃO bloqueia o cadastro do produto (campos ficam vazios).
    const fetchFiscal = async (): Promise<void> => {
      // Chamadas em paralelo, mas com .catch individual: uma falha (ex.: /empresa)
      // não descarta a outra (categorias).
      const categoriasPromise = api
        .get(`/companies/${user.empresa_id}/categorias-fiscais`)
        .catch(() => null);
      const empresaPromise = api
        .get(`/empresa/${user.empresa_id}`)
        .catch(() => null);

      const categoriasRes = await categoriasPromise;
      const empresaRes = await empresaPromise;

      if (categoriasRes) {
        setCategoriasFiscaisOptions(
          (categoriasRes.data || []).map((categoria: any) => ({
            value: Number(categoria.id),
            label: categoria.descricao,
          })),
        );
        setFiscalLoadOk(true);
      }

      if (empresaRes) {
        setEmissaoHabilitada(!!empresaRes.data?.emissao_habilitada);
      }

      // resolvido (sucesso OU falha) p/ o prefill do formulário não travar.
      setFiscalLoaded(true);
    };

    fetchFiscal();
  }, [user.empresa_id]);

  useEffect(() => {
    const defaultGroup = groupsOptions.find((group) => group.value === groupId);
    const defaultUnitOfMeasurement = unitsOfMeasurementOptions.find(
      (unitOfMeasurement) => unitOfMeasurement.value === unitOfMeasurementId,
    );

    // Só faz o prefill único quando (havendo categoria fiscal a pré-selecionar)
    // as opções fiscais já carregaram — senão a categoria não apareceria.
    const fiscalReady =
      !initialSimpleProductData.categoriaFiscalId ||
      categoriasFiscaisOptions.length > 0 ||
      fiscalLoaded;

    if (
      isFirstRender.current &&
      isDrawerOpened &&
      defaultGroup &&
      defaultUnitOfMeasurement &&
      fiscalReady
    ) {
      isFirstRender.current = false;

      formRef.current?.setData({
        name: initialSimpleProductData.name,
        code: initialSimpleProductData.code,
        gtin: initialSimpleProductData.gtin,
        width: initialSimpleProductData.width,
        height: initialSimpleProductData.height,
        length: initialSimpleProductData.length,
        weight: initialSimpleProductData.weight,
        portion: initialSimpleProductData.portion,
        observation: initialSimpleProductData.observation,
        currentStock: initialSimpleProductData.currentStock,
        minimumStock: initialSimpleProductData.minimumStock,
        group: defaultGroup,
        unitOfMeasurement: defaultUnitOfMeasurement,
        categoriaFiscal:
          categoriasFiscaisOptions.find(
            (option) =>
              option.value === initialSimpleProductData.categoriaFiscalId,
          ) || null,
        ncmOverride: initialSimpleProductData.ncmOverride,
        cfopOverride: initialSimpleProductData.cfopOverride,
        cestOverride: initialSimpleProductData.cestOverride,
        origemOverride:
          ORIGEM_OPTIONS.find(
            (option) =>
              String(option.value) ===
              String(initialSimpleProductData.origemOverride),
          ) || null,
      });
    }
  }, [
    formRef,
    groupId,
    groupsOptions,
    isDrawerOpened,
    unitOfMeasurementId,
    initialSimpleProductData,
    unitsOfMeasurementOptions,
    categoriasFiscaisOptions,
    fiscalLoaded,
  ]);

  useEffect(() => {
    if (!isDrawerOpened) {
      isFirstRender.current = true;

      if (formMainRef.current) {
        formMainRef.current.scrollTop = 0;
      }
    }
  }, [isDrawerOpened]);

  const handleSubmit = useCallback(
    async (data) => {
      if (isComplementsDrawerOpened) return;

      setIsLoading(true);

      formRef.current?.setErrors({});

      try {
        const currentTabSchema = SCHEMAS[currentTab];

        const parsedData = {
          ...data,
          minimumStock: Number(data.minimumStock || 0),
          currentStock: Number(data.currentStock || 0),
          addStock: Number(data.addStock || 0),
          price: getNumberValue(data.price),
          sale: getNumberValue(data.sale),
          discount: Number(data.discount),
          portion: data.portion ? Number(data.portion) : null,
          height: data.height ? Number(data.height) : null,
          width: data.width ? Number(data.width) : null,
          length: data.length ? Number(data.length) : null,
          weight: data.weight ? Number(data.weight) : null,
        };

        const formValues = (await currentTabSchema.validate(parsedData, {
          abortEarly: false,
        })) as any;

        if (data.code) {
          const data2 = {
            code: data.code,
          };

          if (typeof id === 'number') {
            Object.assign(data2, {
              productId: id,
            });
          }

          await ProductsService.validatePDVCode(user.empresa_id, data2);
        }

        if (
          initialSimpleProductData.productPortion &&
          Number(data.portion) <= 0
        ) {
          const validationError = new ValidationError(
            ['Fração inválida'],
            formValues.portion,
            'portion',
          );

          validationError.inner = [
            new ValidationError(
              ['Fração inválida'],
              formValues.portion,
              'portion',
              'min',
            ),
          ];

          throw validationError;
        }

        if (
          hasDiscount &&
          !Number.isNaN(formValues.discount) &&
          formValues.discount === 0
        ) {
          const validationError = new ValidationError(
            ['Desconto inválido'],
            formValues.discount,
            'discount',
          );

          validationError.inner = [
            new ValidationError(
              ['Desconto inválido'],
              formValues.discount,
              'discount',
              'min',
            ),
          ];

          throw validationError;
        }

        if (bestShippingIsActive) {
          if (!parsedData.height) {
            const validationError = new ValidationError(
              ['Campo obrigatório'],
              formValues.height,
              'height',
            );

            validationError.inner = [
              new ValidationError(
                ['Campo obrigatório'],
                formValues.height,
                'height',
                'min',
              ),
            ];

            throw validationError;
          }
          if (!parsedData.width) {
            const validationError = new ValidationError(
              ['Campo obrigatório'],
              formValues.width,
              'width',
            );

            validationError.inner = [
              new ValidationError(
                ['Campo obrigatório'],
                formValues.width,
                'width',
                'min',
              ),
            ];

            throw validationError;
          }
          if (!parsedData.length) {
            const validationError = new ValidationError(
              ['Campo obrigatório'],
              formValues.length,
              'length',
            );

            validationError.inner = [
              new ValidationError(
                ['Campo obrigatório'],
                formValues.length,
                'length',
                'min',
              ),
            ];

            throw validationError;
          }
          if (!parsedData.weight) {
            const validationError = new ValidationError(
              ['Campo obrigatório'],
              formValues.weight,
              'weight',
            );

            validationError.inner = [
              new ValidationError(
                ['Campo obrigatório'],
                formValues.weight,
                'weight',
                'min',
              ),
            ];

            throw validationError;
          }
        }

        if (currentTab === LAST_TAB_INDEX || id) {
          await Promise.all(
            Object.values(SCHEMAS).map(async (schema) => {
              await schema.validate(parsedData, {
                abortEarly: false,
              });
            }),
          );

          const invalidShiftExists = details.shifts.find(
            (shift) => shift.startTime > shift.endTime,
          );

          if (!isAwaysAvailability && invalidShiftExists) {
            handleChangeTab(5);

            addToast({
              title: 'Turno inválido',
              description: 'O início do turno deve ser maior do que o fim',
              type: 'error',
            });

            return;
          }

          // Fiscal (Story 1-4): categoria obrigatória quando a empresa emite NFC-e.
          // Só valida se a carga fiscal teve sucesso (senão não sabemos o estado).
          if (fiscalLoadOk && emissaoHabilitada && !data.categoriaFiscal) {
            handleChangeTab(0);

            const validationError = new ValidationError(
              ['Categoria Fiscal é obrigatória quando a emissão está habilitada'],
              data.categoriaFiscal,
              'categoriaFiscal',
            );

            validationError.inner = [
              new ValidationError(
                [
                  'Categoria Fiscal é obrigatória quando a emissão está habilitada',
                ],
                data.categoriaFiscal,
                'categoriaFiscal',
                'required',
              ),
            ];

            throw validationError;
          }

          const formData = new FormData();

          const imagesToUpload: Image[] = [];

          if (image instanceof File) {
            formData.append('image', image.name);
            formData.append('files', image);
          }

          images.forEach((imageToUpload) => {
            if (imageToUpload) {
              if (imageToUpload.file instanceof File) {
                formData.append('files', imageToUpload.file);
                imagesToUpload.push({
                  ...imageToUpload,
                  file: imageToUpload.file.name,
                });
              } else {
                imagesToUpload.push(imageToUpload);
              }
            }
          });

          const parsedComplements = (complements || []).map((complement) => ({
            ...complement,
            id: typeof complement.id === 'number' ? complement.id : undefined,
            items: (complement.items || []).map((item) => {
              const commomItem = {
                ...item,
                id: typeof item.id === 'number' ? item.id : undefined,
                image: undefined,
                price: {
                  value: item.price,
                },
                rawMaterialId:
                  typeof item.rawMaterialId === 'number'
                    ? item.rawMaterialId || undefined
                    : undefined,
              };

              if (item?.image instanceof File) {
                formData.append('files', item.image);

                return {
                  ...commomItem,
                  image: item.image.name,
                };
              }

              return commomItem;
            }),
          }));

          const hasNonFreeComplementItems = parsedComplements.some(
            (complement) =>
              complement.items.some((item) => item.price.value > 0),
          );

          if (
            initialSimpleProductData.productPortion &&
            hasNonFreeComplementItems
          ) {
            handleChangeTab(4);

            addToast({
              title: 'Os complementos precisam ser gratuitos',
              description:
                'Produtos fracionados não podem conter complementos com preço maior do que zero',
              type: 'error',
            });

            return;
          }

          if (typeof parsedData.height === 'number') {
            formData.append('height', parsedData.height);
          }

          if (typeof parsedData.width === 'number') {
            formData.append('width', parsedData.width);
          }

          if (typeof parsedData.length === 'number') {
            formData.append('length', parsedData.length);
          }

          if (typeof parsedData.weight === 'number') {
            formData.append('weight', parsedData.weight);
          }

          if (typeof parsedData.portion === 'number') {
            formData.append('portion', parsedData.portion);
          }

          const parsedDetails = {
            dias: {
              sexta: isAwaysAvailability || details.days.friday,
              terca: isAwaysAvailability || details.days.tuesday,
              quarta: isAwaysAvailability || details.days.wednesday,
              quinta: isAwaysAvailability || details.days.thursday,
              sabado: isAwaysAvailability || details.days.saturday,
              domingo: isAwaysAvailability || details.days.sunday,
              segunda: isAwaysAvailability || details.days.monday,
            },
            turnos: isAwaysAvailability
              ? []
              : details.shifts.map((shift) => ({
                  inicio: shift.startTime,
                  fim: shift.endTime,
                })),
          };

          formData.append('type', '1');
          formData.append('controlGrid', '0');
          formData.append('name', parsedData.name);
          formData.append('groupId', parsedData.group);
          formData.append('code', parsedData.code);
          formData.append('gtin', parsedData.gtin);
          formData.append(
            'productPortion',
            String(initialSimpleProductData.productPortion),
          );
          formData.append('images', JSON.stringify(imagesToUpload));
          formData.append('observation', parsedData.observation);
          formData.append('availability', JSON.stringify(parsedDetails));
          formData.append('minimumStock', parsedData.minimumStock);
          formData.append(
            'currentStock',
            parsedData.currentStock + parsedData.addStock,
          );
          formData.append('complements', JSON.stringify(parsedComplements));
          formData.append('price', JSON.stringify({ value: parsedData.price }));
          formData.append('unitOfMeasurementId', parsedData.unitOfMeasurement);
          formData.append(
            'discount',
            JSON.stringify({
              price: parsedData.price - parsedData.sale,
              value: (parsedData.price - parsedData.sale) / parsedData.price,
              active: hasDiscount,
            }),
          );
          // Fiscal (Story 1-4) — só envia se a carga fiscal teve SUCESSO. Se a API
          // fiscal falhou, NÃO manda os campos: o backend trata ausente como
          // "não alterar", preservando categoria/overrides já salvos (A2).
          if (fiscalLoadOk) {
            // prefill garante o valor atual; string vazia = limpar.
            formData.append(
              'categoriaFiscalId',
              data.categoriaFiscal != null && data.categoriaFiscal !== ''
                ? String(data.categoriaFiscal)
                : '',
            );
            formData.append('ncmOverride', data.ncmOverride || '');
            formData.append('cfopOverride', data.cfopOverride || '');
            formData.append('cestOverride', data.cestOverride || '');
            formData.append(
              'origemOverride',
              data.origemOverride != null && data.origemOverride !== ''
                ? String(data.origemOverride)
                : '',
            );
          }

          let product;

          if (id) {
            product = await ProductsService.editProduct(
              id,
              user.empresa_id,
              parsedData.group,
              formData,
            );
          } else {
            formData.append('order', String(lastProductIndex + 1));
            product = await ProductsService.createProduct(
              user.empresa_id,
              parsedData.group,
              formData,
            );
          }

          let unitOfMeasurementAcronym = '';

          const unitOfMeasurement =
            unitsOfMeasurementOptions.find(
              (foundUnitOfMeasurement) =>
                foundUnitOfMeasurement.value === parsedData.unitOfMeasurement,
            )?.label || '';

          const unitsOfMeasurementMatches = unitOfMeasurement.match(
            /(\(.*?\))|<\w+(\s+("[^"]*"|'[^']*'|[^>])+)?>|<\/\w+>/gi,
          );

          if (unitsOfMeasurementMatches && unitsOfMeasurementMatches.length) {
            unitOfMeasurementAcronym = unitsOfMeasurementMatches[0]
              .replace('(', '')
              .replace(')', '');
          }

          let price = Number(product?.price?.value) || 0;

          if (product?.discount && product?.discount?.active) {
            price -= Number(product.discount.price);
          }

          onSuccessSave(parsedData.group, {
            id: product.id,
            name: product.name,
            stock: {
              currentStock: Number(product.stock.currentStock),
              minimumStock: Number(product.stock.minimumStock),
            },
            order: Number(product.order),
            code: product.code,
            unitOfMeasurement: {
              acronym: unitOfMeasurementAcronym,
            },
            image: product.image,
            price: { value: String(price) },
            pauseSale: Boolean(product.pauseSale),
            numOfGrids: 0,
            grids: [],
          });

          handleCancel();

          return;
        }

        handleChangeTab(currentTab + 1);
      } catch (error) {
        let errors = {};

        if (error instanceof ValidationError) {
          errors = getValidationErrors(error);

          const detailsTabFields = [
            'group',
            'name',
            'observation',
            'unitOfMeasurement',
            'currentStock',
            'minimumStock',
            'gtin',
            'categoriaFiscal',
          ];
          const priceTabFields = ['price', 'sale', 'discount'];
          const dimensionsTabFields = ['height', 'width', 'length', 'weight'];

          if (
            Object.keys(errors).some((field) =>
              detailsTabFields.includes(field),
            )
          ) {
            handleChangeTab(0);
          } else if (
            Object.keys(errors).some((field) => priceTabFields.includes(field))
          ) {
            handleChangeTab(1);
          } else if (
            Object.keys(errors).some((field) =>
              dimensionsTabFields.includes(field),
            )
          ) {
            handleChangeTab(2);
          }

          formRef.current?.setErrors(errors);
        }

        if (error instanceof APIError) {
          if (error.message.includes('Código PDV já em uso pelo produto')) {
            handleChangeTab(0);
            formRef.current?.setErrors({
              code: 'Código já em uso',
            });
          }
          addToast({
            title: error.message,
            type: 'error',
          });
        }
      } finally {
        setIsLoading(false);
      }
    },
    [
      id,
      image,
      images,
      details,
      currentTab,
      hasDiscount,
      complements,
      isAwaysAvailability,
      isComplementsDrawerOpened,
      unitsOfMeasurementOptions,
      initialSimpleProductData.productPortion,
      user.empresa_id,
      lastProductIndex,
      addToast,
      handleCancel,
      onSuccessSave,
      handleChangeTab,
      bestShippingIsActive,
      emissaoHabilitada,
      fiscalLoadOk,
    ],
  );

  return (
    <Container isVisible={isDrawerOpened}>
      <Form ref={formRef} onSubmit={handleSubmit}>
        <header>
          <div className="drawer-title">
            <h1>{id ? 'Editar' : 'Novo'} produto</h1>
            <button
              type="button"
              onClick={handleTryCancel}
              disabled={isLoading}
            >
              <BiX />
            </button>
          </div>
          <TabBar
            selectedTab={currentTab}
            onChange={(_event, value) => handleChangeTab(value)}
          >
            <Tab wrapped>Detalhes</Tab>
            <Tab wrapped>Preço</Tab>
            <Tab wrapped>Dimensões</Tab>
            <Tab wrapped>Imagens</Tab>
            <Tab wrapped>Complementos</Tab>
            <Tab wrapped>Disponibilidade</Tab>
          </TabBar>
        </header>
        <main ref={formMainRef}>
          <TabPanel index={0} selectedTab={currentTab}>
            <Details
              formRef={formRef}
              categoriasFiscaisOptions={categoriasFiscaisOptions}
              emissaoHabilitada={emissaoHabilitada}
            />
          </TabPanel>
          <TabPanel index={1} selectedTab={currentTab}>
            <Price />
          </TabPanel>
          <TabPanel index={2} selectedTab={currentTab}>
            <Dimensions />
          </TabPanel>
          <TabPanel index={3} selectedTab={currentTab}>
            <Images />
          </TabPanel>
          <TabPanel index={4} selectedTab={currentTab}>
            <Complements />
          </TabPanel>
          <TabPanel index={5} selectedTab={currentTab}>
            <Availability />
          </TabPanel>
        </main>
        <footer>
          <Button
            type="button"
            variant="outlined"
            disabled={isLoading}
            onClick={handleTryCancel}
          >
            Cancelar
          </Button>
          <Button type="submit" loading={isLoading}>
            {currentTab === LAST_TAB_INDEX || id ? 'Salvar' : 'Continuar'}
          </Button>
        </footer>
      </Form>
      <Modal ref={modalRef} containerStyle={{ zIndex: 99999999 }}>
        <ModalHeader>
          <div>
            <IoIosWarning />
          </div>
          <h1>Atenção</h1>
          <button type="button" onClick={() => modalRef.current?.close()}>
            <BiX />
          </button>
        </ModalHeader>
        <p>
          Você não salvou suas alterações. Ao realizar a ação todos os dados
          serão perdidos.
          <br />
          <strong>Deseja prosseguir?</strong>
        </p>
        <Button full onClick={handleCancel}>
          Confirmar
        </Button>
        <Button
          full
          variant="outlined"
          onClick={() => modalRef.current?.close()}
        >
          Cancelar
        </Button>
      </Modal>
    </Container>
  );
};

export default SimpleProduct;
